import React, { useState, useRef, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  TrashIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  CloudArrowUpIcon,
  ExclamationCircleIcon,
  PencilIcon,
  EyeIcon,
  StarIcon as StarOutlineIcon,
  LinkIcon,
  CodeBracketIcon,
  DocumentIcon,
  WrenchScrewdriverIcon,
  CubeTransparentIcon,
  PuzzlePieceIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useStarterTemplates } from '../../contexts/StarterTemplateContext';
import { StarterTemplate } from '../../firebase/starterTemplateService';
import { validateCloudinaryConfig } from '../../services/cloudinaryService';


const StarterTemplateSchema = Yup.object().shape({
  title: Yup.string()
    .required('Title is required')
    .max(100, 'Title is too long'),
  description: Yup.string()
    .required('Description is required')
    .max(200, 'Description is too long'),
  longDescription: Yup.string()
    .required('Long description is required'),
  type: Yup.string()
    .oneOf(['template', 'extension', 'tool', 'boilerplate'], 'Invalid type')
    .required('Type is required'),
  category: Yup.string()
    .required('Category is required'),
  features: Yup.array()
    .of(Yup.string().required('Feature is required'))
    .min(1, 'At least one feature is required'),
  technologies: Yup.array()
    .of(Yup.string().required('Technology is required'))
    .min(1, 'At least one technology is required'),
  tags: Yup.array()
    .of(Yup.string().required('Tag is required'))
    .min(1, 'At least one tag is required'),
  installation: Yup.string()
    .required('Installation instructions are required'),
  usage: Yup.string()
    .required('Usage instructions are required'),
  requirements: Yup.array()
    .of(Yup.string().required('Requirement is required'))
    .min(1, 'At least one requirement is required'),
  benefits: Yup.array()
    .of(Yup.string().required('Benefit is required'))
    .min(1, 'At least one benefit is required'),
  useCases: Yup.array()
    .of(Yup.string().required('Use case is required'))
    .min(1, 'At least one use case is required'),
  author: Yup.string()
    .required('Author is required'),
  githubUrl: Yup.string()
    .url('Must be a valid URL'),
  downloadUrl: Yup.string()
    .url('Must be a valid URL'),
  demoUrl: Yup.string()
    .url('Must be a valid URL'),
});

const initialValues: Omit<StarterTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  title: '',
  description: '',
  longDescription: '',
  type: 'template',
  category: '',
  icon: '',
  coverImage: '',
  screenshots: [],
  features: [''],
  technologies: [''],
  tags: [''],
  githubUrl: '',
  downloadUrl: '',
  demoUrl: '',
  documentationUrl: '',
  installation: '',
  usage: '',
  changelog: '',
  version: '1.0.0',
  license: 'MIT',
  author: '',
  contributors: [],
  downloads: 0,
  stars: 0,
  forks: 0,
  issues: 0,
  lastUpdated: '',
  requirements: [''],
  benefits: [''],
  useCases: [''],
  featured: false,
  order: 0,
};

const typeOptions = [
  { value: 'template', label: 'Template', icon: DocumentIcon, color: 'bg-blue-100 text-blue-800' },
  { value: 'extension', label: 'Extension', icon: PuzzlePieceIcon, color: 'bg-green-100 text-green-800' },
  { value: 'tool', label: 'Tool', icon: WrenchScrewdriverIcon, color: 'bg-yellow-100 text-yellow-800' },
  { value: 'boilerplate', label: 'Boilerplate', icon: CubeTransparentIcon, color: 'bg-purple-100 text-purple-800' },
];

const categoryOptions = [
  'Flutter', 'React', 'Node.js', 'Python', 'Java', 'Swift', 'Kotlin',
  'VS Code Extension', 'Chrome Extension', 'CLI Tool', 'Web Template',
  'Mobile Template', 'Desktop App', 'API Template', 'Database Schema',
  'Docker Template', 'CI/CD Template', 'Other'
];

const StarterTemplatesEditor: React.FC = () => {
  const { starterTemplates, loading, error, addStarterTemplate, editStarterTemplate, removeStarterTemplate, moveUp, moveDown } = useStarterTemplates();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<StarterTemplate | null>(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [uploading, setUploading] = useState(false);
  const [selectedIconFile, setSelectedIconFile] = useState<File | null>(null);
  const [selectedCoverImageFile, setSelectedCoverImageFile] = useState<File | null>(null);
  const [selectedScreenshotFiles, setSelectedScreenshotFiles] = useState<File[]>([]);
  const [cloudinaryConfigValid, setCloudinaryConfigValid] = useState(true);
  
  const iconInputRef = useRef<HTMLInputElement>(null);
  const coverImageInputRef = useRef<HTMLInputElement>(null);
  const screenshotInputRef = useRef<HTMLInputElement>(null);

  // Check Cloudinary configuration on component mount
  useEffect(() => {
    const config = validateCloudinaryConfig();
    if (!config.isValid) {
      setCloudinaryConfigValid(false);
      console.warn('Cloudinary configuration incomplete. Missing:', config.missingVars);
      toast.error(`Cloudinary setup incomplete. Missing environment variables: ${config.missingVars.join(', ')}`);
    }
  }, []);

  const filteredTemplates = starterTemplates.filter(template => {
    const typeMatch = filterType === 'all' || template.type === filterType;
    const categoryMatch = filterCategory === 'all' || template.category === filterCategory;
    return typeMatch && categoryMatch;
  });

  const handleSubmit = async (values: typeof initialValues, { setSubmitting, resetForm }: any) => {
    try {
      console.log('Starting form submission with values:', values);
      
      // Validate required arrays are not empty after filtering empty strings
      const validFeatures = values.features.filter(f => f.trim() !== '');
      const validTechnologies = values.technologies.filter(t => t.trim() !== '');
      const validTags = values.tags.filter(t => t.trim() !== '');
      const validRequirements = values.requirements.filter(r => r.trim() !== '');
      const validBenefits = values.benefits.filter(b => b.trim() !== '');
      const validUseCases = values.useCases.filter(u => u.trim() !== '');
      
      if (validFeatures.length === 0) {
        toast.error('At least one feature is required');
        return;
      }
      if (validTechnologies.length === 0) {
        toast.error('At least one technology is required');
        return;
      }
      if (validTags.length === 0) {
        toast.error('At least one tag is required');
        return;
      }
      if (validRequirements.length === 0) {
        toast.error('At least one requirement is required');
        return;
      }
      if (validBenefits.length === 0) {
        toast.error('At least one benefit is required');
        return;
      }
      if (validUseCases.length === 0) {
        toast.error('At least one use case is required');
        return;
      }

      // Clean up the values
      const cleanedValues = {
        ...values,
        features: validFeatures,
        technologies: validTechnologies,
        tags: validTags,
        requirements: validRequirements,
        benefits: validBenefits,
        useCases: validUseCases,
        screenshots: Array.isArray(values.screenshots) ? values.screenshots : [],
        contributors: Array.isArray(values.contributors) ? values.contributors : [],
      };

      console.log('Cleaned values:', cleanedValues);

      if (editingTemplate) {
        console.log('Updating template with ID:', editingTemplate.id);
        await editStarterTemplate(editingTemplate.id!, cleanedValues);
        toast.success('Template updated successfully!');
      } else {
        console.log('Creating new template');
        
        // Check if Cloudinary is configured for image uploads
        if (!cloudinaryConfigValid && (cleanedValues.icon || cleanedValues.coverImage || (cleanedValues.screenshots && cleanedValues.screenshots.length > 0))) {
          toast('Template will be created without images due to missing Cloudinary configuration.', {
            icon: '⚠️',
            duration: 4000,
          });
        }
        
        const newId = await addStarterTemplate(cleanedValues);
        console.log('Template created with ID:', newId);
        toast.success('Template created successfully!');
      }
      
      setIsModalOpen(false);
      setEditingTemplate(null);
      setSelectedIconFile(null);
      setSelectedCoverImageFile(null);
      setSelectedScreenshotFiles([]);
      resetForm();
    } catch (err: any) {
      console.error('Error saving template:', err);
      
      // Show more specific error messages
      let errorMessage = 'Failed to save template. Please try again.';
      
      if (err?.message?.includes('Cloudinary')) {
        errorMessage = 'Image upload failed. Please check your network connection and try again.';
      } else if (err?.message?.includes('cloud name')) {
        errorMessage = 'Image upload service not configured properly. Please contact support.';
      } else if (err?.message?.includes('Firebase')) {
        errorMessage = 'Database connection failed. Please check your internet connection.';
      } else if (err?.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your access rights.';
      } else if (err?.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (template: StarterTemplate) => {
    setEditingTemplate(template);
    setSelectedIconFile(null);
    setSelectedCoverImageFile(null);
    setSelectedScreenshotFiles([]);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await removeStarterTemplate(id);
      toast.success('Template deleted successfully!');
      setDeleteConfirmationId(null);
    } catch (err) {
      console.error('Error deleting template:', err);
      toast.error('Failed to delete template. Please try again.');
    }
  };

  const handleMoveUp = async (id: string) => {
    try {
      await moveUp(id);
      toast.success('Template moved up!');
    } catch (err) {
      console.error('Error moving template up:', err);
      toast.error('Failed to move template.');
    }
  };

  const handleMoveDown = async (id: string) => {
    try {
      await moveDown(id);
      toast.success('Template moved down!');
    } catch (err) {
      console.error('Error moving template down:', err);
      toast.error('Failed to move template.');
    }
  };

  const handleIconFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    
    if (supportedTypes.includes(file.type) || file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedIconFile(file);
    } else {
      toast.error('Please select a valid image file (JPG, PNG, GIF, WebP, SVG)');
    }
    event.target.value = '';
  };

  const handleCoverImageFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    
    if (supportedTypes.includes(file.type) || file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedCoverImageFile(file);
    } else {
      toast.error('Please select a valid image file (JPG, PNG, GIF, WebP, SVG)');
    }
    event.target.value = '';
  };

  const handleScreenshotFilesSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const validFiles: File[] = [];
    
    for (const file of files) {
      if (supportedTypes.includes(file.type) || file.type.startsWith('image/')) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name}: File size must be less than 5MB`);
          continue;
        }
        validFiles.push(file);
      } else {
        toast.error(`${file.name}: Please select a valid image file`);
      }
    }
    
    setSelectedScreenshotFiles(prev => [...prev, ...validFiles]);
    event.target.value = '';
  };

  const uploadIconFile = async (setFieldValue: any) => {
    if (!selectedIconFile) return;
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setFieldValue('icon', result);
        setSelectedIconFile(null);
        setUploading(false);
        toast.success('Icon uploaded successfully!');
      };
      reader.onerror = () => {
        setUploading(false);
        toast.error('Failed to read file. Please try again.');
      };
      reader.readAsDataURL(selectedIconFile);
    } catch (error) {
      setUploading(false);
      toast.error('Failed to upload icon. Please try again.');
    }
  };

  const uploadCoverImageFile = async (setFieldValue: any) => {
    if (!selectedCoverImageFile) return;
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setFieldValue('coverImage', result);
        setSelectedCoverImageFile(null);
        setUploading(false);
        toast.success('Cover image uploaded successfully!');
      };
      reader.onerror = () => {
        setUploading(false);
        toast.error('Failed to read file. Please try again.');
      };
      reader.readAsDataURL(selectedCoverImageFile);
    } catch (error) {
      setUploading(false);
      toast.error('Failed to upload cover image. Please try again.');
    }
  };

  const uploadScreenshotFiles = async (setFieldValue: any, currentValues: any) => {
    if (selectedScreenshotFiles.length === 0) return;
    
    setUploading(true);
    try {
      const uploadPromises = selectedScreenshotFiles.map((file) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            resolve(result);
          };
          reader.onerror = () => {
            reject(new Error(`Failed to read ${file.name}`));
          };
          reader.readAsDataURL(file);
        });
      });
      
      const results = await Promise.all(uploadPromises);
      const currentScreenshots = Array.isArray(currentValues.screenshots) ? currentValues.screenshots : [];
      const newScreenshots = [...currentScreenshots, ...results];
      
      setFieldValue('screenshots', newScreenshots);
      setSelectedScreenshotFiles([]);
      setUploading(false);
      toast.success(`${results.length} screenshot(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error during upload process:', error);
      setUploading(false);
      toast.error('Failed to upload some screenshots. Please try again.');
    }
  };

  const removeSelectedIconFile = () => {
    setSelectedIconFile(null);
  };

  const removeSelectedCoverImageFile = () => {
    setSelectedCoverImageFile(null);
  };

  const removeSelectedScreenshotFile = (index: number) => {
    setSelectedScreenshotFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getTypeIcon = (type: string) => {
    const typeOption = typeOptions.find(option => option.value === type);
    return typeOption ? typeOption.icon : DocumentIcon;
  };

  const getTypeColor = (type: string) => {
    const typeOption = typeOptions.find(option => option.value === type);
    return typeOption ? typeOption.color : 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading starter templates</h3>
            <p className="mt-2 text-sm text-red-700">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

      return (
    <div className="space-y-6">
      {/* Cloudinary Configuration Warning */}
      {!cloudinaryConfigValid && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Image Upload Not Configured</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Cloudinary environment variables are missing. Image uploads will fail.</p>
                <p className="mt-1">Please add <code className="bg-yellow-100 px-1 rounded">REACT_APP_CLOUDINARY_CLOUD_NAME</code> and <code className="bg-yellow-100 px-1 rounded">REACT_APP_CLOUDINARY_UPLOAD_PRESET</code> to your .env file.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Starter Templates & Tools</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Manage your development templates, tools, extensions, and boilerplates to help other developers.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => {
              setEditingTemplate(null);
              setSelectedIconFile(null);
              setSelectedCoverImageFile(null);
              setSelectedScreenshotFiles([]);
              setIsModalOpen(true);
            }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Template/Tool
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filter by Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            >
              <option value="all">All Types</option>
              {typeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filter by Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
            >
              <option value="all">All Categories</option>
              {categoryOptions.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredTemplates.map((template, index) => {
            const TypeIcon = getTypeIcon(template.type);
            
            return (
              <li key={template.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Template Icon/Cover */}
                    <div className="flex-shrink-0">
                      {template.coverImage ? (
                        <img
                          className="h-16 w-16 rounded-lg object-cover"
                          src={template.coverImage}
                          alt={template.title}
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <TypeIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Template Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                          {template.title}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(template.type)}`}>
                          {template.type}
                        </span>
                        {template.featured && (
                          <StarSolidIcon className="h-5 w-5 text-yellow-400" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {template.description}
                      </p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="text-xs text-gray-400">
                          Category: {template.category}
                        </span>
                        {template.version && (
                          <span className="text-xs text-gray-400">
                            v{template.version}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {template.downloads || 0} downloads
                        </span>
                        {template.stars && (
                          <span className="text-xs text-gray-400">
                            ⭐ {template.stars}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    {template.githubUrl && (
                      <a
                        href={template.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                        title="View on GitHub"
                      >
                        <LinkIcon className="h-5 w-5" />
                      </a>
                    )}
                    
                    <button
                      onClick={() => handleEdit(template)}
                      className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    
                    <button
                      onClick={() => handleMoveUp(template.id!)}
                      disabled={index === 0}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ArrowUpIcon className="h-5 w-5" />
                    </button>
                    
                    <button
                      onClick={() => handleMoveDown(template.id!)}
                      disabled={index === filteredTemplates.length - 1}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ArrowDownIcon className="h-5 w-5" />
                    </button>
                    
                    <button
                      onClick={() => setDeleteConfirmationId(template.id!)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        
        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No templates found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {filterType !== 'all' || filterCategory !== 'all' 
                ? 'No templates match the current filters.' 
                : 'Get started by creating your first template or tool.'}
            </p>
          </div>
        )}
      </div>

      {/* Modal for Add/Edit Template */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedIconFile(null);
                setSelectedCoverImageFile(null);
                setSelectedScreenshotFiles([]);
              }}
            />

            <div
              className="relative bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    {editingTemplate ? 'Edit Template/Tool' : 'Add New Template/Tool'}
                  </h3>
                </div>

                <Formik
                  initialValues={editingTemplate ? {
                    ...editingTemplate,
                    screenshots: editingTemplate.screenshots || [],
                    features: editingTemplate.features.length > 0 ? editingTemplate.features : [''],
                    technologies: editingTemplate.technologies.length > 0 ? editingTemplate.technologies : [''],
                    tags: editingTemplate.tags.length > 0 ? editingTemplate.tags : [''],
                    requirements: editingTemplate.requirements.length > 0 ? editingTemplate.requirements : [''],
                    benefits: editingTemplate.benefits.length > 0 ? editingTemplate.benefits : [''],
                    useCases: editingTemplate.useCases.length > 0 ? editingTemplate.useCases : [''],
                    contributors: editingTemplate.contributors || [],
                  } : initialValues}
                  validationSchema={StarterTemplateSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                  validateOnChange={false}
                  validateOnBlur={false}
                >
                  {({ values, setFieldValue, isSubmitting }) => (
                    <Form className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Basic Information */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Title *
                          </label>
                          <Field
                            name="title"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="e.g., Flutter Starter Template"
                          />
                          <ErrorMessage name="title" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Type *
                          </label>
                          <Field
                            as="select"
                            name="type"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          >
                            {typeOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage name="type" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Category *
                          </label>
                          <Field
                            as="select"
                            name="category"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          >
                            <option value="">Select a category</option>
                            {categoryOptions.map(category => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage name="category" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Version
                          </label>
                          <Field
                            name="version"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="1.0.0"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Short Description *
                          </label>
                          <Field
                            name="description"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="A brief description of your template/tool"
                          />
                          <ErrorMessage name="description" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Long Description *
                          </label>
                          <Field
                            as="textarea"
                            name="longDescription"
                            rows={4}
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="Detailed description with features, use cases, and benefits"
                          />
                          <ErrorMessage name="longDescription" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* URLs */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            GitHub URL
                          </label>
                          <Field
                            name="githubUrl"
                            type="url"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://github.com/username/repository"
                          />
                          <ErrorMessage name="githubUrl" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Download URL
                          </label>
                          <Field
                            name="downloadUrl"
                            type="url"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://releases.github.com/..."
                          />
                          <ErrorMessage name="downloadUrl" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Demo URL
                          </label>
                          <Field
                            name="demoUrl"
                            type="url"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://demo.example.com"
                          />
                          <ErrorMessage name="demoUrl" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Documentation URL
                          </label>
                          <Field
                            name="documentationUrl"
                            type="url"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://docs.example.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Author *
                          </label>
                          <Field
                            name="author"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="Your name"
                          />
                          <ErrorMessage name="author" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            License
                          </label>
                          <Field
                            name="license"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="MIT"
                          />
                        </div>

                        {/* Icon Upload */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Icon
                          </label>
                          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                            {values.icon ? (
                              <div className="flex items-center space-x-4">
                                <div className="relative">
                                  <img
                                    src={values.icon}
                                    alt="Template icon"
                                    className="h-20 w-20 rounded-lg object-cover"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setFieldValue('icon', '')}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    title="Remove icon"
                                  >
                                    <TrashIcon className="h-3 w-3" />
                                  </button>
                                </div>
                                <div className="flex flex-col space-y-2">
                                  <button
                                    type="button"
                                    onClick={() => iconInputRef.current?.click()}
                                    disabled={uploading}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Change Icon
                                  </button>
                                </div>
                              </div>
                            ) : selectedIconFile ? (
                              <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                                      <CloudArrowUpIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                        {selectedIconFile.name}
                                      </p>
                                      <p className="text-xs text-blue-700 dark:text-blue-400">
                                        {(selectedIconFile.size / 1024 / 1024).toFixed(2)} MB
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={removeSelectedIconFile}
                                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                    title="Remove selected file"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                                <div className="flex space-x-3">
                                  <button
                                    type="button"
                                    onClick={() => uploadIconFile(setFieldValue)}
                                    disabled={uploading}
                                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    {uploading ? 'Uploading...' : 'Upload Icon'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => iconInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    Change
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center">
                                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <div className="mt-4">
                                  <button
                                    type="button"
                                    onClick={() => iconInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Select Icon
                                  </button>
                                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    PNG, JPG, GIF up to 5MB
                                  </p>
                                </div>
                              </div>
                            )}
                            <input
                              ref={iconInputRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleIconFileSelect}
                            />
                          </div>
                        </div>

                        {/* Cover Image Upload */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Cover Image
                          </label>
                          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                            {values.coverImage ? (
                              <div className="flex items-center space-x-4">
                                <div className="relative">
                                  <img
                                    src={values.coverImage}
                                    alt="Cover image"
                                    className="h-20 w-20 rounded-lg object-cover"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setFieldValue('coverImage', '')}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    title="Remove cover image"
                                  >
                                    <TrashIcon className="h-3 w-3" />
                                  </button>
                                </div>
                                <div className="flex flex-col space-y-2">
                                  <button
                                    type="button"
                                    onClick={() => coverImageInputRef.current?.click()}
                                    disabled={uploading}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Change Cover
                                  </button>
                                </div>
                              </div>
                            ) : selectedCoverImageFile ? (
                              <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                                      <CloudArrowUpIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-green-900 dark:text-green-300">
                                        {selectedCoverImageFile.name}
                                      </p>
                                      <p className="text-xs text-green-700 dark:text-green-400">
                                        {(selectedCoverImageFile.size / 1024 / 1024).toFixed(2)} MB
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={removeSelectedCoverImageFile}
                                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                    title="Remove selected file"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                                <div className="flex space-x-3">
                                  <button
                                    type="button"
                                    onClick={() => uploadCoverImageFile(setFieldValue)}
                                    disabled={uploading}
                                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    {uploading ? 'Uploading...' : 'Upload Cover'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => coverImageInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    Change
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center">
                                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <div className="mt-4">
                                  <button
                                    type="button"
                                    onClick={() => coverImageInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Select Cover Image
                                  </button>
                                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    PNG, JPG, GIF up to 5MB
                                  </p>
                                </div>
                              </div>
                            )}
                            <input
                              ref={coverImageInputRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleCoverImageFileSelect}
                            />
                          </div>
                        </div>

                        {/* Screenshots */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Screenshots
                          </label>
                          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                            {Array.isArray(values.screenshots) && values.screenshots.length > 0 ? (
                              <div className="space-y-4">
                                <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                                  {values.screenshots.map((screenshot, index) => (
                                    <div key={index} className="relative group">
                                      <img
                                        src={screenshot}
                                        alt={`Screenshot ${index + 1}`}
                                        className="w-full h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const currentScreenshots = Array.isArray(values.screenshots) ? values.screenshots : [];
                                          const newScreenshots = currentScreenshots.filter((_, i) => i !== index);
                                          setFieldValue('screenshots', newScreenshots);
                                        }}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Remove screenshot"
                                      >
                                        <TrashIcon className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex justify-center pt-4 border-t border-gray-200 dark:border-gray-600">
                                  <button
                                    type="button"
                                    onClick={() => screenshotInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Add More Screenshots
                                  </button>
                                </div>
                              </div>
                            ) : selectedScreenshotFiles.length > 0 ? (
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  {selectedScreenshotFiles.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                                          <CloudArrowUpIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-purple-900 dark:text-purple-300">
                                            {file.name}
                                          </p>
                                          <p className="text-xs text-purple-700 dark:text-purple-400">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                          </p>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeSelectedScreenshotFile(index)}
                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                        title="Remove selected file"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex space-x-3">
                                  <button
                                    type="button"
                                    onClick={() => uploadScreenshotFiles(setFieldValue, values)}
                                    disabled={uploading}
                                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    {uploading ? 'Uploading...' : `Upload ${selectedScreenshotFiles.length} Screenshot${selectedScreenshotFiles.length !== 1 ? 's' : ''}`}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => screenshotInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    Add More
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center">
                                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <div className="mt-4">
                                  <button
                                    type="button"
                                    onClick={() => screenshotInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Select Screenshots
                                  </button>
                                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    PNG, JPG, GIF up to 5MB each • Multiple files supported
                                  </p>
                                </div>
                              </div>
                            )}
                            <input
                              ref={screenshotInputRef}
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={handleScreenshotFilesSelect}
                            />
                          </div>
                        </div>

                        {/* Installation Instructions */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Installation Instructions *
                          </label>
                          <Field
                            as="textarea"
                            name="installation"
                            rows={4}
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="Step-by-step installation guide..."
                          />
                          <ErrorMessage name="installation" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Usage Instructions */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Usage Instructions *
                          </label>
                          <Field
                            as="textarea"
                            name="usage"
                            rows={4}
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="How to use this template/tool..."
                          />
                          <ErrorMessage name="usage" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Dynamic Arrays */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Features *
                          </label>
                          <FieldArray name="features">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {Array.isArray(values.features) && values.features.map((_, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <Field
                                      name={`features.${index}`}
                                      type="text"
                                      className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                      placeholder="Describe a key feature"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => push('')}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Feature
                                </button>
                              </div>
                            )}
                          </FieldArray>
                          <ErrorMessage name="features" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Technologies */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Technologies *
                          </label>
                          <FieldArray name="technologies">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {Array.isArray(values.technologies) && values.technologies.map((_, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <Field
                                      name={`technologies.${index}`}
                                      type="text"
                                      className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                      placeholder="e.g., Flutter, React, Node.js"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => push('')}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Technology
                                </button>
                              </div>
                            )}
                          </FieldArray>
                          <ErrorMessage name="technologies" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Tags */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Tags *
                          </label>
                          <FieldArray name="tags">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {Array.isArray(values.tags) && values.tags.map((_, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <Field
                                      name={`tags.${index}`}
                                      type="text"
                                      className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                      placeholder="e.g., starter, template, boilerplate"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => push('')}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Tag
                                </button>
                              </div>
                            )}
                          </FieldArray>
                          <ErrorMessage name="tags" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Requirements */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Requirements *
                          </label>
                          <FieldArray name="requirements">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {Array.isArray(values.requirements) && values.requirements.map((_, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <Field
                                      name={`requirements.${index}`}
                                      type="text"
                                      className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                      placeholder="e.g., Node.js 16+, Flutter SDK"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => push('')}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Requirement
                                </button>
                              </div>
                            )}
                          </FieldArray>
                          <ErrorMessage name="requirements" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Benefits */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Benefits *
                          </label>
                          <FieldArray name="benefits">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {Array.isArray(values.benefits) && values.benefits.map((_, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <Field
                                      name={`benefits.${index}`}
                                      type="text"
                                      className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                      placeholder="e.g., Faster development, Better code structure"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => push('')}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Benefit
                                </button>
                              </div>
                            )}
                          </FieldArray>
                          <ErrorMessage name="benefits" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Use Cases */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Use Cases *
                          </label>
                          <FieldArray name="useCases">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {Array.isArray(values.useCases) && values.useCases.map((_, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <Field
                                      name={`useCases.${index}`}
                                      type="text"
                                      className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                      placeholder="e.g., Building mobile apps, Creating REST APIs"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => push('')}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Use Case
                                </button>
                              </div>
                            )}
                          </FieldArray>
                          <ErrorMessage name="useCases" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Statistics */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Downloads
                            </label>
                            <Field
                              name="downloads"
                              type="number"
                              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Stars
                            </label>
                            <Field
                              name="stars"
                              type="number"
                              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                              placeholder="0"
                            />
                          </div>
                        </div>

                        {/* Featured */}
                        <div className="md:col-span-2">
                          <div className="flex items-center">
                            <Field
                              name="featured"
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                              Featured Template/Tool
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Featured items will be highlighted on the public page
                              </p>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Modal Actions */}
                      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <button
                          type="button"
                          onClick={() => {
                            setIsModalOpen(false);
                            setSelectedIconFile(null);
                            setSelectedCoverImageFile(null);
                            setSelectedScreenshotFiles([]);
                          }}
                          className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {isSubmitting ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmationId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setDeleteConfirmationId(null)}
            />

            <div
              className="relative bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Delete Template/Tool
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this template/tool? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => handleDelete(deleteConfirmationId)}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmationId(null)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default StarterTemplatesEditor; 